import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Link,
  InputAdornment,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import {
  Email as EmailIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Twitter as TwitterIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
}));

const ContactCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: 0,
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
  }
}));

const SocialLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  color: theme.palette.text.primary,
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  marginBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': { 
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    '& .arrow-icon': {
      transform: 'translateX(4px)',
      opacity: 1,
    }
  },
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
}));

const ArrowIcon = styled(ArrowForwardIcon)(({ theme }) => ({
  marginLeft: 'auto',
  fontSize: 14,
  opacity: 0.3,
  transition: 'all 0.2s ease',
}));

const FormTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.secondary.main,
      borderWidth: '1px',
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.text.secondary,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: '0.8rem',
    '&.Mui-focused': {
      color: theme.palette.secondary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(1),
    fontSize: '0.8rem',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.75, 2),
  backgroundColor: 'transparent',
  color: theme.palette.secondary.main,
  fontWeight: 400,
  letterSpacing: '0.05em',
  borderRadius: 0,
  border: `1px solid ${theme.palette.secondary.main}`,
  fontSize: '0.7rem',
  textTransform: 'lowercase',
  '&:hover': {
    backgroundColor: 'rgba(100, 255, 218, 0.05)',
    '& .send-icon': {
      transform: 'translateX(3px)',
    }
  },
  '& .send-icon': {
    transition: 'transform 0.2s ease',
    fontSize: '0.8rem',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  fontWeight: 600,
  letterSpacing: '-0.01em',
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
}));

const ThankYouMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
}));

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Check if form was submitted in current session
  useEffect(() => {
    const formSubmitted = sessionStorage.getItem('contactFormSubmitted');
    if (formSubmitted === 'true') {
      setSubmitted(true);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Function to mark form as submitted - will be called with an onClick event
  // This avoids interfering with the form's native submission
  const markAsSubmitted = () => {
    sessionStorage.setItem('contactFormSubmitted', 'true');
    // We'll set submitted state after a small delay to allow the form to submit first
    setTimeout(() => {
      setSubmitted(true);
      setSuccess(true);
    }, 100);
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 50,
        damping: 10,
      },
    },
  };

  return (
    <SectionContainer id="contact">
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box sx={{ mb: 4 }}>
            <motion.div variants={itemVariants}>
              <SectionTitle variant="h2">
                Contact
              </SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '450px' }}>
                Interested in working together? Feel free to reach out.
              </Typography>
            </motion.div>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <ContactCard>
                  <Typography variant="h6" gutterBottom fontWeight={500} sx={{ 
                    mb: 2, 
                    fontSize: '0.9rem',
                    letterSpacing: '0.05em',
                    position: 'relative',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    textTransform: 'lowercase'
                  }}>
                    {submitted ? 'message sent' : 'send a message'}
                  </Typography>
                  
                  {submitted ? (
                    <ThankYouMessage>
                      <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Thank you for reaching out!
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        I've received your message and will get back to you as soon as possible.
                      </Typography>
                    </ThankYouMessage>
                  ) : (
                    <form 
                      action="https://formowl.dev/api/@/Ndp5gw" 
                      method="POST"
                    >
                      <input name="_success_url" value={window.location.href} style={{ display: 'none' }} />
                      <FormTextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ fontSize: '0.9rem' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormTextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ fontSize: '0.9rem' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormTextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={3}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <MessageIcon sx={{ fontSize: '0.9rem' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <SubmitButton
                        type="submit"
                        variant="outlined"
                        endIcon={<SendIcon className="send-icon" />}
                        disabled={loading}
                        onClick={markAsSubmitted}
                      >
                        {loading ? 'sending...' : 'send message'}
                      </SubmitButton>
                    </form>
                  )}
                </ContactCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <ContactCard>
                  <Typography variant="h6" gutterBottom fontWeight={500} sx={{ 
                    mb: 2, 
                    fontSize: '0.9rem',
                    letterSpacing: '0.05em',
                    position: 'relative',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    textTransform: 'lowercase'
                  }}>
                    contact info
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <SocialLink href="mailto:your.email@example.com">
                      <SocialIcon>
                        <EmailIcon sx={{ fontSize: '0.9rem' }} />
                      </SocialIcon>
                      <Box>
                        <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                          Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          ak21367185@gmail.com
                        </Typography>
                      </Box>
                      <ArrowIcon className="arrow-icon" />
                    </SocialLink>

                    <SocialLink href="https://github.com/kumarav1nash" target="_blank" rel="noopener noreferrer">
                      <SocialIcon>
                        <GitHubIcon sx={{ fontSize: '0.9rem' }} />
                      </SocialIcon>
                      <Box>
                        <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                          GitHub
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          github.com/kumarav1nash
                        </Typography>
                      </Box>
                      <ArrowIcon className="arrow-icon" />
                    </SocialLink>

                    <SocialLink href="https://linkedin.com/in/kumarav1nash" target="_blank" rel="noopener noreferrer">
                      <SocialIcon>
                        <LinkedInIcon sx={{ fontSize: '0.9rem' }} />
                      </SocialIcon>
                      <Box>
                        <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                          LinkedIn
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          linkedin.com/in/kumarav1nash
                        </Typography>
                      </Box>
                      <ArrowIcon className="arrow-icon" />
                    </SocialLink>

                    {/* <SocialLink href="https://twitter.com/" target="_blank" rel="noopener noreferrer">
                      <SocialIcon>
                        <TwitterIcon sx={{ fontSize: '0.9rem' }} />
                      </SocialIcon>
                      <Box>
                        <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                          Twitter
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          twitter.com/yourusername
                        </Typography>
                      </Box>
                      <ArrowIcon className="arrow-icon" />
                    </SocialLink> */}
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                      Prefer to schedule a meeting?
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      component={Link}
                      href="https://calendly.com/kumarav1nash/30min"
                      target="_blank"
                      sx={{ 
                        borderRadius: 0,
                        textTransform: 'lowercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.7rem',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      schedule a call
                    </Button>
                  </Box>
                </ContactCard>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
      
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 0, fontSize: '0.8rem' }}>
          Message sent successfully!
        </Alert>
      </Snackbar>
      
      <Snackbar open={error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', borderRadius: 0, fontSize: '0.8rem' }}>
          Something went wrong. Please try again.
        </Alert>
      </Snackbar>
    </SectionContainer>
  );
};

export default Contact;